import { Timestamp } from '@britishcouncil/common-core';
import {
  DateRangeFilter,
  SimpleFiltersObjectValues,
  CustomMultiSelectFilter,
  MultiSelectOption,
  MultiSelectFilter,
} from '@britishcouncil/react-admin';
import { OsmMonitoringDto, OsmMonitoringStatus, OsmMonitoringType } from 'api';

type OsmMonitoringFiltersDefinition = {
  status: CustomMultiSelectFilter<OsmMonitoringStatus>;
  date: DateRangeFilter;
  type: MultiSelectFilter<OsmMonitoringType>;
};

export type OsmMonitoringFiltersValues = SimpleFiltersObjectValues<OsmMonitoringFiltersDefinition>;

const STATUS_OPTIONS: MultiSelectOption<OsmMonitoringStatus>[] = [
  { text: 'Current', value: OsmMonitoringStatus.Current, color: 'green' },
  { text: 'Archived', value: OsmMonitoringStatus.Archived, color: 'yellow' },
  { text: 'Deleted', value: OsmMonitoringStatus.Deleted, color: 'red' },
];

const getOsmMonitoringTypeName = (type: OsmMonitoringType): string => {
  const typeNames: Record<OsmMonitoringType, string> = {
    [OsmMonitoringType.Osm]: 'OSM',
    [OsmMonitoringType.Eor]: 'EOR',
    [OsmMonitoringType.Rem]: 'REM',
  };
  return typeNames[type] ?? '-';
};

const TYPE_OPTIONS: MultiSelectOption<OsmMonitoringType>[] = [
  { text: getOsmMonitoringTypeName(OsmMonitoringType.Osm), value: OsmMonitoringType.Osm },
  { text: getOsmMonitoringTypeName(OsmMonitoringType.Eor), value: OsmMonitoringType.Eor },
  { text: getOsmMonitoringTypeName(OsmMonitoringType.Rem), value: OsmMonitoringType.Rem },
];

export const FILTERS_DEFINITION: OsmMonitoringFiltersDefinition = {
  status: {
    type: 'custom-multiselect',
    label: 'Status',
    options: STATUS_OPTIONS,
    isMain: true,
    dropdownOptions: {
      placeholder: 'Select monitoring status...',
      selectedOnTop: true,
      isClearable: true,
    },
  },
  date: {
    isMain: true,
    type: 'daterange',
    label: 'Date range',
    datePickerOptions: {
      placeholder: 'Select monitoring dates...',
      isClearable: true,
    },
  },
  type: {
    type: 'multiselect',
    label: 'Type of monitoring',
    isMain: true,
    options: TYPE_OPTIONS,
    dropdownOptions: {
      placeholder: 'Select type...',
      selectedOnTop: true,
      isClearable: true,
    },
  },
};

export const filterMonitorings = (
  monitorings: OsmMonitoringDto[] | undefined,
  filters: OsmMonitoringFiltersValues
) => {
  let filtered = monitorings ?? [];

  // Filter by status
  if (filters.status && filters.status.length > 0) {
    filtered = filtered?.filter((monitoring) => filters.status!.includes(monitoring.status));
  }

  // Filter by date range
  if (filters.date && filters.date.length > 0) {
    const [startDate, endDate] = filters.date;

    if (endDate) {
      // Date range selected
      filtered = filtered.filter((monitoring) => {
        const monitoringDate = Timestamp.parse(monitoring.monitoringDate);
        return monitoringDate >= startDate && monitoringDate <= endDate;
      });
    } else {
      // Only start date selected
      filtered = filtered.filter((monitoring) => {
        const monitoringDate = Timestamp.parse(monitoring.monitoringDate);
        return monitoringDate >= startDate;
      });
    }
  }

  // Filter by type
  if (filters.type && filters.type.length > 0) {
    filtered = filtered?.filter((monitoring) => filters.type!.includes(monitoring.type));
  }

  return filtered;
};
